import type { ErrorParsed, AxiosError, ErrorReason } from 'typings'
import type { InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import guid from 'utils/guid'
import config from 'const/config'
import headers from 'const/headers'
import sessionKeys from 'const/sessionKeys'

const getErrorFromArray = (errors: ErrorReason[], message: string): string => {
  const startMessage = message ? `${message}: ` : ''
  const joinErrors = errors?.map(err => err.reason).join(', ')
  return startMessage + joinErrors
}

const getUserError = (userMessage: { [key: string]: string }, message: string): string => {
  const startMessage = message ? `${message}: ` : ''
  const joinErrors = Object.keys(userMessage)
    .map(err => {
      return userMessage[err]
    })
    .join(', ')
  return startMessage + joinErrors
}

export const parseError = (error?: AxiosError): ErrorParsed | AxiosError | undefined => {
  const submissionError =
    'Our system is experiencing technical difficulties at the moment. Please try again later or call us later at 844-894-3960.'

  if (error?.response) {
    const { status, data } = error.response
    const { code = '', message = '', errors, userMessage } = data
    let errorMessage = submissionError
    if (code === 'gateway.client.authenticationFailure') {
      errorMessage = 'Authentication Failure: Please try again.'
    }
    if (code === 'gateway.client.badRequest') {
      errorMessage = 'Recaptcha validation failed: Please try again.'
    }
    if (code === 'gateway.internal.error') {
      errorMessage = message
    }
    if (errors?.length) {
      errorMessage = getErrorFromArray(errors, message)
    }
    if (userMessage && Object.keys(userMessage).length) {
      errorMessage = getUserError(userMessage, message)
    }

    return {
      statusCode: status,
      description: errorMessage,
      businessErrorCode: code,
      errors
    } as ErrorParsed
  }

  return error
}


/**
 * All request with this instance will use authentication token
 */
const requestBuilder = axios.create({
  baseURL: config.SERVICE_URL
})

// request interceptors occur as the very last step before request is sent to server
requestBuilder.interceptors.request.use(async (reqConfig: InternalAxiosRequestConfig) => {

  // fill content type if it's empty
  if (!reqConfig.headers.get(headers.contentType)) {
    reqConfig.headers.set(headers.contentType, 'application/json')
  }

  // if a request hasn't provided a custom trackingId
  // we provide a unique one
  if (!reqConfig.headers.get(headers.trackingId)) {
    reqConfig.headers.set(headers.trackingId, guid.generate())
  }

  return reqConfig
})

// response interceptors occur as the very first step when a response is received
requestBuilder.interceptors.response.use(
  res => res,
  err => {
    throw parseError(err)
  }
)

export default requestBuilder
